import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faTimes, faChevronDown, faSearch, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TravelDocumentService } from 'projects/clooper/core/src/public-api';
import { SuccessModalComponent } from '../success-modal/success-modal.component';
import { countryCodesAndFlags } from '../../shared/utils/constants';
import { idNumberValidator, expiryDateValidator, issueDateValidator } from '../../shared/validators/id-document.validators';

const DOCUMENT_TYPE_MAP: Record<string, string> = {
  'International Passport': 'passport',
  'National ID': 'national_id',
  'Driver\'s License': 'drivers_license',
  'Visa': 'visa',
  'Residence Permit': 'residence_permit'
};

@Component({
  selector: 'app-add-document-modal',
  templateUrl: './add-document-modal.component.html',
  styleUrls: ['./add-document-modal.component.scss']
})
export class AddDocumentModalComponent implements OnInit {
  icons = { close: faTimes, chevronDown: faChevronDown, search: faSearch, warn: faExclamationTriangle };
  documentForm: FormGroup;
  submitting = false;
  nameMismatchWarning = '';

  countries = countryCodesAndFlags;
  filteredCountries = countryCodesAndFlags;
  countrySearch = '';
  showCountryDropdown = false;
  selectedCountry: any = null;


  documentTypes: string[] = [
    'International Passport', 'National ID', 'Driver\'s License', 'Visa', 'Residence Permit'
  ];

  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private travelDocumentService: TravelDocumentService,
    private modalService: BsModalService,
    private elRef: ElementRef
  ) {}

  // Set via initialState (profile data for name matching)
  profileFirstName = '';
  profileLastName = '';
  onAdded?: () => void;

  ngOnInit(): void {
    this.documentForm = this.fb.group({
      country: ['', Validators.required],
      type: ['', Validators.required],
      first_name: ['', [Validators.required, Validators.minLength(1)]],
      last_name: ['', [Validators.required, Validators.minLength(1)]],
      id_number: ['', [
        Validators.required,
        idNumberValidator(
          () => this.documentForm?.get('type')?.value || '',
          () => this.selectedCountry?.code || ''
        )
      ]],
      expiry_date: ['', [Validators.required, expiryDateValidator]],
      issue_date: ['', [
        issueDateValidator(() => this.documentForm?.get('expiry_date')?.value || '')
      ]],
    });

    // Re-validate id_number when type or country changes
    this.documentForm.get('type')?.valueChanges.subscribe(() => {
      this.documentForm.get('id_number')?.updateValueAndValidity();
    });
  }

  checkNameMismatch(): void {
    if (!this.profileFirstName && !this.profileLastName) {
      this.nameMismatchWarning = '';
      return;
    }
    const fn = (this.documentForm.get('first_name')?.value || '').trim().toLowerCase();
    const ln = (this.documentForm.get('last_name')?.value || '').trim().toLowerCase();
    const pfn = this.profileFirstName.trim().toLowerCase();
    const pln = this.profileLastName.trim().toLowerCase();
    if (fn && ln && (fn !== pfn || ln !== pln)) {
      this.nameMismatchWarning = 'The name on this document does not match your profile name. Please verify it is correct.';
    } else {
      this.nameMismatchWarning = '';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elRef.nativeElement.querySelector('.country-dropdown-wrapper')?.contains(event.target)) {
      this.showCountryDropdown = false;
    }
  }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) {
      this.countrySearch = '';
      this.filteredCountries = this.countries;
    }
  }

  filterCountries(): void {
    const term = this.countrySearch.toLowerCase();
    this.filteredCountries = this.countries.filter(
      c => c.name?.toLowerCase().includes(term)
    );
  }

  getCountryFlagUrl(country: any): string {
    const code = (country?.code || '').toUpperCase();
    return code ;
  }

  onFlagError(event: Event, countryCode: string): void {
    const img = event.target as HTMLImageElement | null;
    if (!img) return;

    const fallbackUrl = countryCode ? `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png` : '';
    if (fallbackUrl && img.src !== fallbackUrl) {
      img.src = fallbackUrl;
      return;
    }

    img.style.visibility = 'hidden';
  }

  selectCountry(country: any): void {
    this.selectedCountry = country;
    this.documentForm.get('country')?.setValue(country.name);
    this.showCountryDropdown = false;
    // Re-validate id_number for country-specific rules
    this.documentForm.get('id_number')?.updateValueAndValidity();
  }

  close(): void {
    this.bsModalRef?.hide();
  }

  addDocument(): void {
    if (this.documentForm.invalid || this.submitting) return;

    this.submitting = true;
    const form = this.documentForm.value;

    const payload: any = {
      country: this.selectedCountry?.code?.toLowerCase() || form.country.trim().toLowerCase(),
      type: DOCUMENT_TYPE_MAP[form.type] || form.type,
      firstName: form.first_name.trim(),
      lastName: form.last_name.trim(),
      documentNumber: form.id_number.trim(),
      expirationDate: form.expiry_date,
      ...(form.issue_date ? { issueDate: form.issue_date } : {})
    };

    this.travelDocumentService.addTravelDocumentFullResponse(payload).subscribe({
      next: () => {
        this.submitting = false;
        this.onAdded?.();
        this.close();
        this.modalService.show(SuccessModalComponent, {
          initialState: {
            title: 'Document Added!',
            subtitle: 'Your travel document has been added successfully.'
          },
          class: 'modal-rounded'
        });
      },
      error: () => {
        this.submitting = false;
      }
    });
  }
}
